import { useCallback, useSyncExternalStore } from 'react';

const SELECTED_CLIENT_KEY = 'mv_selected_client';

export interface SelectedClient {
  id: string | number;
  [key: string]: unknown;
}

export interface UseSelectedClientResult {
  selectedClient: SelectedClient | null;
  selectClient: (client: SelectedClient) => void;
  deselectClient: () => void;
  hasSelectedClient: boolean;
}

type Listener = () => void;

const listeners = new Set<Listener>();

const emitChange = (): void => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const readFromStorage = (): SelectedClient | null => {
  try {
    const stored = localStorage.getItem(SELECTED_CLIENT_KEY);

    return stored ? (JSON.parse(stored) as SelectedClient) : null;
  } catch {
    return null;
  }
};

let cachedSnapshot: SelectedClient | null = readFromStorage();

const getSnapshot = (): SelectedClient | null => cachedSnapshot;

const setClient = (client: SelectedClient): void => {
  try {
    localStorage.setItem(SELECTED_CLIENT_KEY, JSON.stringify(client));
  } catch {
    // silently ignore storage errors
  }
  cachedSnapshot = client;
  emitChange();
};

const clearClient = (): void => {
  try {
    localStorage.removeItem(SELECTED_CLIENT_KEY);
  } catch {
    // silently ignore storage errors
  }
  cachedSnapshot = null;
  emitChange();
};

export const useSelectedClient = (): UseSelectedClientResult => {
  const selectedClient = useSyncExternalStore(subscribe, getSnapshot);

  const selectClient = useCallback((client: SelectedClient) => {
    setClient(client);
  }, []);

  const deselectClient = useCallback(() => {
    clearClient();
  }, []);

  return {
    selectedClient,
    selectClient,
    deselectClient,
    hasSelectedClient: !!selectedClient,
  };
};
